import React, { useState } from 'react';
import { useDecimalDraft } from './useDecimalDraft.ts';

interface NumberInputProps {
    label?: React.ReactNode;
    name?: string;
    value: number | string | null | undefined;
    onChange: (value: number | undefined) => void;
    className?: string;
    placeholder?: string;
    id?: string;
    disabled?: boolean;
}

/**
 * Plain numeric input for bare counts/terms/years — anything that is neither a dollar amount
 * (CurrencyInput) nor a percentage/rate (PercentageInput). Uses a text input rather than
 * type="number" so a stray '$' or '%' actually reaches us: either means the user has confused this
 * for a currency or percentage field, so we reject the keystroke and flag the mistake rather than
 * let the browser silently swallow it. Always parses to a number on change, unlike FormInput's
 * `type="number"` mode which only does so when the caller remembers to pass that prop.
 */
const NumberInput = ({ label, name, value, onChange, className, placeholder, id, disabled }: NumberInputProps) => {
    const [error, setError] = useState('');
    // Shows what the user is typing while they type it, so a decimal point survives — see the hook.
    const { displayValue, setDraft, syncOnBlur } = useDecimalDraft(value);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (raw.includes('$')) {
            setError('This is a plain number, not a dollar amount — remove the "$".');
            return;
        }
        if (raw.includes('%')) {
            setError('This is a plain number, not a percentage — remove the "%".');
            return;
        }
        setError('');
        // Kept AFTER the rejections above, so a rejected keystroke is never held as the draft.
        setDraft(raw);
        if (raw === '') {
            onChange(undefined);
            return;
        }
        const parsed = parseFloat(raw);
        onChange(Number.isNaN(parsed) ? undefined : parsed);
    };
    return (
        <div className={className}>
            {label && <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
            <input
                type="text"
                inputMode="decimal"
                id={id}
                name={name}
                value={displayValue}
                onChange={handleChange}
                onBlur={syncOnBlur}
                placeholder={placeholder}
                disabled={disabled}
                className="w-full px-4 py-2 bg-white/80 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export default NumberInput;
